import { list, get, insert, update, softDelete, reviveOrInsert, run, unwrap, mapLimit, fail, isLive, isTrue, rowKey, same } from "./scaffold";
import { EVAL_TABLES, EVAL_VIEWS, SCOPE_TYPES } from "../model/constants";

// Evaluator scope: grants, areas, and the directory reads that feed them (contract C5,
// spec §6.3, §9.6).
//
// Every export resolves to { ok:true, data } | { ok:false, error } and never throws.
//
// People come from vw_EvalV2_UserDirectory only — never UserMst (no passwords/refresh tokens
// over an unauthenticated API, and UserMst is ~19 MB) and never AccessLevelTran.

const T = EVAL_TABLES;
const V = EVAL_VIEWS;
const isNil = (v) => v === null || v === undefined;
const SCOPES = Object.values(SCOPE_TYPES);
const byText = (key) => (a, b) => String(a?.[key] || "").localeCompare(String(b?.[key] || ""), "en");
/** IsActive is a real column on the legacy tables; anything but an explicit false counts as active. */
const activeRow = (r) => r && r.IsActive !== false && r.IsActive !== 0 && r.IsActive !== "0" && r.IsActive !== "false";
const uniqIds = (ids) => {
  const out = [];
  for (const id of ids) if (id && !out.some((x) => same(x, id))) out.push(String(id).trim());
  return out;
};

// ── Grants ─────────────────────────────────────────────────────────────────

/** listGrants({districtId}) — live grants made in a district. */
export async function listGrants({ districtId } = {}) {
  if (!districtId) return fail("Choose a district.");
  return run(async () => unwrap(await list(T.evaluatorGrant, { SchoolDistrictId: districtId })));
}

/** listGrantsForUser(userId) — live grants held by one evaluator (any district). */
export async function listGrantsForUser(userId) {
  if (!userId) return fail("User id is required.");
  return run(async () => unwrap(await list(T.evaluatorGrant, { EvaluatorUserId: userId })));
}

const grantKeyMatch = (row) => (r) =>
  same(r.EvaluatorUserId, row.EvaluatorUserId) &&
  same(r.SchoolDistrictId, row.SchoolDistrictId) &&
  String(r.ScopeType || "") === String(row.ScopeType || "") &&
  (isNil(row.SchoolId) ? isNil(r.SchoolId) : same(r.SchoolId, row.SchoolId)) &&
  (isNil(row.AreaId) ? isNil(r.AreaId) : same(r.AreaId, row.AreaId));

/** Problems with one grant row; [] when it is well-formed. */
export function validateGrant(row) {
  const errors = [];
  if (!row?.EvaluatorUserId) errors.push("Choose the evaluator");
  if (!row?.SchoolDistrictId) errors.push("Choose the district");
  if (!SCOPES.includes(row?.ScopeType)) errors.push("Choose what they can evaluate");
  if (row?.ScopeType === SCOPE_TYPES.SITE && !row.SchoolId) errors.push("Choose a site");
  if (row?.ScopeType === SCOPE_TYPES.AREA && !row.AreaId) errors.push("Choose an area");
  return errors;
}

/**
 * addGrants(rows, by) — one row per scope unit ({EvaluatorUserId, SchoolDistrictId,
 * ScopeType, SchoolId?, AreaId?, Note?}). Revive-or-insert on the natural key, so granting
 * the same site twice, or again after removal, never duplicates.
 * -> {grants, inserted, revived, existing}
 */
export async function addGrants(rows, by) {
  const clean = (rows || []).map((r) => ({
    EvaluatorUserId: r.EvaluatorUserId,
    SchoolDistrictId: r.SchoolDistrictId,
    ScopeType: r.ScopeType,
    SchoolId: r.ScopeType === SCOPE_TYPES.SITE ? r.SchoolId ?? null : null,
    AreaId: r.ScopeType === SCOPE_TYPES.AREA ? r.AreaId ?? null : null,
    Note: r.Note ?? null,
  }));
  if (!clean.length) return fail("Nothing to grant.");
  const errors = [...new Set(clean.flatMap(validateGrant))];
  if (errors.length) return fail(errors.join(". "), { errors });
  return run(async () => {
    const counts = { inserted: 0, revived: 0, existing: 0 };
    const grants = [];
    for (const row of clean) {
      const res = unwrap(
        await reviveOrInsert(
          T.evaluatorGrant,
          grantKeyMatch(row),
          { ...row, CreatedBy: by ?? null, ModifiedBy: by ?? null },
          { EvaluatorUserId: row.EvaluatorUserId }
        )
      );
      counts[res.action] += 1;
      grants.push(res.row);
    }
    return { grants, ...counts };
  });
}

export async function removeGrant(grantId, by) {
  if (!grantId) return fail("Grant id is required.");
  return run(async () => unwrap(await softDelete(T.evaluatorGrant, grantId, by)));
}

// ── Areas ──────────────────────────────────────────────────────────────────

export async function listAreas({ districtId } = {}) {
  if (!districtId) return fail("Choose a district.");
  return run(async () => unwrap(await list(T.area, { SchoolDistrictId: districtId })).sort(byText("Name")));
}

/** upsertArea({AreaId?, SchoolDistrictId, Name, ManagerUserId?}, by) -> area row. */
export async function upsertArea(area, by) {
  const name = typeof area?.Name === "string" ? area.Name.trim() : "";
  if (!area?.SchoolDistrictId) return fail("Choose a district.");
  if (!name) return fail("Area name is required.", { errors: ["Area name is required"] });
  return run(async () => {
    const id = rowKey(area, T.area);
    const existing = id ? unwrap(await get(T.area, id)) : null;
    const fields = { SchoolDistrictId: area.SchoolDistrictId, Name: name, ManagerUserId: area.ManagerUserId || null };
    if (existing) {
      const patch = { ...fields, IsDeleted: false, ModifiedBy: by ?? null };
      unwrap(await update(T.area, rowKey(existing, T.area), patch));
      return { ...existing, ...patch };
    }
    return unwrap(await insert(T.area, { ...fields, ...(id ? { AreaId: id } : {}), CreatedBy: by ?? null }));
  });
}

/** deleteArea(areaId, by) — soft-deletes the area and its site links. Area grants then resolve to nobody. */
export async function deleteArea(areaId, by) {
  if (!areaId) return fail("Area id is required.");
  return run(async () => {
    const sites = unwrap(await list(T.areaSite, { AreaId: areaId }));
    for (const s of sites) unwrap(await softDelete(T.areaSite, rowKey(s, T.areaSite), by));
    unwrap(await softDelete(T.area, areaId, by));
    return { areaId, sitesRemoved: sites.length };
  });
}

/**
 * setAreaSites(areaId, schoolIds, by) — makes the area's live sites exactly `schoolIds`:
 * revives dead links, inserts new ones, soft-deletes the rest.
 * -> {sites (live rows), inserted, revived, removed}
 */
export async function setAreaSites(areaId, schoolIds, by) {
  if (!areaId) return fail("Area id is required.");
  const wanted = uniqIds(schoolIds || []);
  return run(async () => {
    const existing = unwrap(await list(T.areaSite, { AreaId: areaId }, { includeDeleted: true }));
    const counts = { inserted: 0, revived: 0, removed: 0 };
    const sites = [];
    const keep = new Set();
    for (const schoolId of wanted) {
      const matches = existing.filter((r) => same(r.SchoolId, schoolId));
      const live = matches.find(isLive);
      const dead = matches[0];
      if (live) {
        keep.add(String(rowKey(live, T.areaSite)).toUpperCase());
        sites.push(live);
      } else if (dead) {
        keep.add(String(rowKey(dead, T.areaSite)).toUpperCase());
        const patch = { IsDeleted: false, ModifiedBy: by ?? null };
        unwrap(await update(T.areaSite, rowKey(dead, T.areaSite), patch));
        sites.push({ ...dead, ...patch });
        counts.revived += 1;
      } else {
        sites.push(unwrap(await insert(T.areaSite, { AreaId: areaId, SchoolId: schoolId, CreatedBy: by ?? null })));
        counts.inserted += 1;
      }
    }
    for (const r of existing) {
      if (!isLive(r) || keep.has(String(rowKey(r, T.areaSite)).toUpperCase())) continue;
      unwrap(await softDelete(T.areaSite, rowKey(r, T.areaSite), by));
      counts.removed += 1;
    }
    return { sites, ...counts };
  });
}

/**
 * listSitesForAreas(areaIds) — live EvalV2_AreaSite rows for the given areas. One read per
 * area up to 2 areas; beyond that one read of the whole (small) table, filtered here.
 */
export async function listSitesForAreas(areaIds) {
  const ids = uniqIds(areaIds || []);
  if (!ids.length) return run(async () => []);
  return run(async () => {
    if (ids.length > 2) {
      const all = unwrap(await list(T.areaSite));
      return all.filter((r) => ids.some((id) => same(id, r.AreaId)));
    }
    const lists = await mapLimit(ids, 2, async (id) => unwrap(await list(T.areaSite, { AreaId: id })));
    return lists.flat();
  });
}

/** listAreaSites({districtId}) — live area-site rows for every live area in a district. */
export async function listAreaSites({ districtId } = {}) {
  if (!districtId) return fail("Choose a district.");
  return run(async () => {
    const areas = unwrap(await list(T.area, { SchoolDistrictId: districtId }));
    return unwrap(await listSitesForAreas(areas.map((a) => rowKey(a, T.area))));
  });
}

/** {AreaId: SchoolId[]} from area-site rows — the shape permissions.buildContext takes. */
export function areaSitesMap(rows) {
  const map = {};
  for (const r of rows || []) {
    if (!isLive(r) || !r.AreaId || !r.SchoolId) continue;
    const key = String(r.AreaId).trim().toUpperCase();
    (map[key] = map[key] || []).push(r.SchoolId);
  }
  return map;
}

// ── Directory + reference reads ────────────────────────────────────────────

/** listSites({districtId}) — active SchoolMst rows for a district, by name. */
export async function listSites({ districtId } = {}) {
  if (!districtId) return fail("Choose a district.");
  return run(async () =>
    unwrap(await list("SchoolMst", { SchoolDistrictId: districtId }, { includeDeleted: true }))
      .filter(activeRow)
      .sort(byText("SchoolName"))
  );
}

/**
 * listDirectory({districtId | schoolId | userId}) — vw_EvalV2_UserDirectory (active users,
 * no credentials). With no filter it reads the whole view (Super Admin only). By FullName.
 */
export async function listDirectory({ districtId, schoolId, userId } = {}) {
  const params = {};
  if (userId) params.UserId = userId;
  else if (schoolId) params.SchoolId = schoolId;
  else if (districtId) params.SchoolDistrictId = districtId;
  return run(async () =>
    unwrap(await list(V.userDirectory, params, { includeDeleted: true })).filter(activeRow).sort(byText("FullName"))
  );
}

/** listAdditionalDistricts(userId) -> SchoolDistrictId[] from active AdditionalSchoolDistrict rows. */
export async function listAdditionalDistricts(userId) {
  if (!userId) return fail("User id is required.");
  return run(async () =>
    uniqIds(
      unwrap(await list("AdditionalSchoolDistrict", { UserId: userId }, { includeDeleted: true }))
        .filter(activeRow)
        .map((r) => r.SchoolDistrictId)
    )
  );
}

/** listPortfolioDistricts(userId) -> SchoolDistrictId[] the user administers through a portfolio
 * (vw_AccountAllocatedDistricts is already filtered to live allocations, groups and districts). */
export async function listPortfolioDistricts(userId) {
  if (!userId) return fail("User id is required.");
  return run(async () => uniqIds(unwrap(await list("vw_AccountAllocatedDistricts", { UserId: userId })).map((r) => r.SchoolDistrictId)));
}

/** listDirectReports(userId) -> UserId[] from active UserHierarchy rows where they are the manager. */
export async function listDirectReports(userId) {
  if (!userId) return fail("User id is required.");
  return run(async () =>
    uniqIds(
      unwrap(await list("UserHierarchy", { ManagerUserId: userId }, { includeDeleted: true }))
        .filter((r) => isTrue(r.IsActive))
        .map((r) => r.UserId)
    )
  );
}

/**
 * listDistricts() -> [{id, name}] of active districts, by name. Partner districts are NOT
 * filtered out: this feeds user-facing pickers, not reports.
 */
export async function listDistricts() {
  return run(async () =>
    unwrap(await list("SchoolDistrict", {}, { includeDeleted: true }))
      .filter((d) => isTrue(d.IsActive) && d.SchoolDistrictId)
      .map((d) => ({ id: d.SchoolDistrictId, name: d.DistrictName || "" }))
      .sort(byText("name"))
  );
}

const evaluatorsApi = {
  listGrants,
  listGrantsForUser,
  addGrants,
  removeGrant,
  listAreas,
  upsertArea,
  deleteArea,
  setAreaSites,
  listAreaSites,
  listSitesForAreas,
  areaSitesMap,
  listSites,
  listDirectory,
  listAdditionalDistricts,
  listDirectReports,
  listDistricts,
};
export default evaluatorsApi;
